import {Badge, Code, Group, Stack, Text} from '@mantine/core';
import {AiOutlineExclamationCircle, AiOutlineInfoCircle} from 'react-icons/ai';
import {AnalysisFinding, FindingSeverity} from '../types';
import {severityColor} from './analysisFindingGroups';

const severityIcon = (severity: FindingSeverity) =>
    severity === 'WARNING' ? <AiOutlineExclamationCircle/> : <AiOutlineInfoCircle/>;

interface AnalysisFindingItemProps {
    finding: AnalysisFinding;
}

/**
 * A single line of the analyzer's reasoning, rendered as
 * "<label> → <value>, read from <source>, because <evidence>".
 */
export const AnalysisFindingItem = ({finding}: AnalysisFindingItemProps) => (
    <Stack
        component='li'
        gap={4}
        style={{listStyle: 'none'}}
        data-testid={`analysis-finding-${finding.key}`}
    >
        <Group gap='xs' wrap='wrap'>
            <Badge
                size='xs'
                variant='light'
                color={severityColor(finding.severity)}
                leftSection={severityIcon(finding.severity)}
                aria-label={`Severity ${finding.severity}`}
            >
                {finding.severity}
            </Badge>
            <Text size='sm' fw={500} span>{finding.label}</Text>
            {finding.value && <Code>{finding.value}</Code>}
        </Group>

        <Group gap={6} wrap='nowrap' align='flex-start' pl='md'>
            <Text size='xs' c='dimmed' fw={500} span style={{whiteSpace: 'nowrap'}}>
                Read from
            </Text>
            <Code data-testid={`analysis-finding-source-${finding.key}`}>
                {finding.source}
            </Code>
        </Group>

        {finding.evidence && (
            <Group gap={6} wrap='nowrap' align='flex-start' pl='md'>
                <Text size='xs' c='dimmed' fw={500} span style={{whiteSpace: 'nowrap'}}>
                    Evidence
                </Text>
                <Text
                    size='xs'
                    c='dimmed'
                    span
                    style={{wordBreak: 'break-word'}}
                    data-testid={`analysis-finding-evidence-${finding.key}`}
                >
                    {finding.evidence}
                </Text>
            </Group>
        )}
    </Stack>
);

interface AnalysisFindingsListProps {
    findings: AnalysisFinding[];
    /** Accessible name for the list, e.g. the decision group it belongs to. */
    'aria-label'?: string;
}

/**
 * Renders analyzer findings as a semantic list. Non-interactive by design —
 * evidence is always readable, never behind a click.
 */
export const AnalysisFindingsList = ({
                                         findings,
                                         'aria-label': ariaLabel,
                                     }: AnalysisFindingsListProps) => {
    if (findings.length === 0) return null;

    return (
        <Stack
            component='ul'
            gap='sm'
            aria-label={ariaLabel}
            style={{listStyle: 'none', margin: 0, padding: 0}}
        >
            {findings.map((finding) => (
                <AnalysisFindingItem key={finding.key} finding={finding}/>
            ))}
        </Stack>
    );
};
